import { useEffect } from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { useInView } from 'react-intersection-observer';

import * as content from 'assets/content/home';
import useMedia from 'utils/useMedia';
import { breakpoints } from 'Components/ThemeProvider';
import * as flagVariants from 'dux/featureFlags/constants/flagVariants';
import * as userSelectors from 'dux/user/selectors';

import TextImageSlider from 'Blocks/TextImageSlider';
import ConsultationCTA from 'Containers/ConsultationCTA';

import JsonLd from 'Components/Jsonld';
import ScrollTracker from 'Containers/ScrollTracker';

import HowItWorks from 'Blocks/HowItWorks';
import Ticker from 'Blocks/Ticker';

import BaseScene from 'Scenes/LegacyBaseScene';
import useAttentiveAbTest from 'abTesting/hooks/attentive';

import HomeHero from './HomeHero';
import WelcomeCustomCare from './WelcomeCustomCare';
import Collection from './Collection';
import Bcorp from './Bcorp';
import CustomersReviews from './CustomersReviews';
import PressSlider from './PressSlider';
import UgcBlock from './UgcBlock';
import Softfooter from './Softfooter';

const Home = ({ header, footer, userEmail }) => {
  const isMobile = useMedia(`(max-width: ${breakpoints.sm}px)`);
  const [heroRef, heroInView] = useInView({ threshold: 0.7 });
  const { onChoice, flagsDoneFetching, isInExperiment, variantName } = useAttentiveAbTest();

  useEffect(() => {
    if (flagsDoneFetching && isInExperiment) {
      onChoice();
      setTimeout(() => {
        if (variantName === flagVariants.VARIANT2) {
          window.__attentive?.trigger?.(userEmail, null, null, isMobile ? 164449 : 164450);
        }
      }, 2000);
    }
  }, [isInExperiment, flagsDoneFetching, variantName]);

  return (
    <BaseScene footer={footer} header={header} relativePosition>
      <JsonLd>{content.dataJsonLd}</JsonLd>
      <ScrollTracker>
        <HomeHero ref={heroRef} />
        <Ticker content={content.ticker} />
        <WelcomeCustomCare content={content.welcomeCustomCare} />
        <Collection content={content.collection} />
        <HowItWorks
          backgroundColor="veryLightVert"
          content={content.howItWorks}
          isMobileDevice={isMobile}
        />
        <TextImageSlider content={content.carousel} dataFrom="block-consultation-slider" />
        <Bcorp content={content.bcorp} />
        <CustomersReviews content={content.customers} />
        <PressSlider content={content.press} />
        <UgcBlock content={content.ugc} />
        <Softfooter content={content.softFooter} />
        {isMobile && !heroInView && <ConsultationCTA dataFrom="sticky-footer" fixed theme="vert" />}
      </ScrollTracker>
    </BaseScene>
  );
};

Home.defaultProps = {
  footer: true,
  header: true,
  userEmail: null,
};

Home.propTypes = {
  footer: PropTypes.bool,
  header: PropTypes.bool,
  userEmail: PropTypes.string,
};

const ConnectedHome = connect(
  state => ({
    userEmail: userSelectors.getUserEmail(state),
  }),
  {}
)(Home);

export default ConnectedHome;
